import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for open settings event from footer
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowSettings(true);
    };
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  const acceptAll = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    
    // Update Google Consent Mode
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    
    setShowBanner(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected');
    
    // Ensure analytics remains denied
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    
    setShowBanner(false);
    setShowSettings(false);
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  // Get current consent status for settings modal
  const currentConsent = localStorage.getItem(CONSENT_KEY);

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && !showSettings && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
            data-testid="cookie-banner"
          >
            <div className="max-w-[1400px] mx-auto">
              <div className="bg-[#1E2A24] border border-[#A6B8A2]/20 p-6 md:p-8 shadow-2xl">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Cookie size={20} className="text-[#A6B8A2]" />
                      <h3 className="text-white font-medium">Vi bruger cookies</h3>
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">
                      Vi bruger cookies til at analysere trafik og forbedre din oplevelse på siden. 
                      Vælg "Acceptér alle" for at tillade analyticscookies, eller "Kun nødvendige" 
                      for kun at tillade teknisk nødvendige cookies.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={rejectAll}
                      className="px-6 py-3 text-sm border border-white/30 text-white hover:bg-white/10 transition-colors"
                      data-testid="cookie-reject-btn"
                    >
                      Kun nødvendige
                    </button>
                    <button
                      onClick={acceptAll}
                      className="px-6 py-3 text-sm bg-white text-[#1E2A24] hover:bg-[#F4F2EE] transition-colors"
                      data-testid="cookie-accept-btn"
                    >
                      Acceptér alle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            data-testid="cookie-settings-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#F4F2EE] max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[#A6B8A2]/20 flex items-center justify-between">
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24]">
                  Cookie indstillinger
                </h2>
                <button
                  onClick={closeSettings}
                  className="text-[#1E2A24]/50 hover:text-[#1E2A24] transition-colors"
                  data-testid="cookie-settings-close"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <p className="text-[#1E2A24]/70 text-sm leading-relaxed">
                    Vi bruger cookies til at forbedre din oplevelse på vores hjemmeside. 
                    Du kan vælge hvilke typer cookies du vil tillade.
                  </p>
                </div>

                {/* Necessary cookies */}
                <div className="p-4 bg-white border border-[#A6B8A2]/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-[#0E3D24]">Nødvendige cookies</h3>
                    <span className="text-xs text-[#A6B8A2] bg-[#A6B8A2]/10 px-2 py-1">Altid aktive</span>
                  </div>
                  <p className="text-sm text-[#1E2A24]/70">
                    Disse cookies er nødvendige for at hjemmesiden kan fungere korrekt. 
                    De kan ikke deaktiveres.
                  </p>
                </div>

                {/* Analytics cookies */}
                <div className="p-4 bg-white border border-[#A6B8A2]/20">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-[#0E3D24]">Analytics cookies</h3>
                    <span className={`text-xs px-2 py-1 ${
                      currentConsent === 'accepted' 
                        ? 'text-[#0E3D24] bg-[#0E3D24]/10' 
                        : 'text-[#1E2A24]/50 bg-[#1E2A24]/5'
                    }`}>
                      {currentConsent === 'accepted' ? 'Tilladt' : 'Afvist'}
                    </span>
                  </div>
                  <p className="text-sm text-[#1E2A24]/70">
                    Disse cookies hjælper os med at forstå hvordan besøgende bruger hjemmesiden, 
                    så vi kan forbedre indhold og funktioner. (Google Analytics)
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={rejectAll}
                    className="flex-1 px-6 py-3 text-sm border border-[#0E3D24] text-[#0E3D24] hover:bg-[#0E3D24]/5 transition-colors"
                    data-testid="cookie-settings-reject"
                  >
                    Kun nødvendige
                  </button>
                  <button
                    onClick={acceptAll}
                    className="flex-1 px-6 py-3 text-sm bg-[#0E3D24] text-white hover:bg-[#1F5A3A] transition-colors"
                    data-testid="cookie-settings-accept"
                  >
                    Acceptér alle
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Export function to open settings from anywhere (e.g., footer link)
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent('openCookieSettings'));
}

// Hook for components that need to listen for the open event
export function useCookieSettings() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('openCookieSettings', handleOpen);
    return () => window.removeEventListener('openCookieSettings', handleOpen);
  }, []);

  return { isOpen, setIsOpen };
}

export default CookieConsent;
